import { appSettingsService } from '../AppSettingsService';
import type { AppSettings } from '../../models/appSettings';

/**
 * Enhanced service configuration that loads from appsettings files
 * This replaces the old serviceConfig.ts with dynamic configuration loading
 */
export class AppServiceConfig {
  private static instance: AppServiceConfig;
  private settings: AppSettings | null = null;

  private constructor() {}

  public static getInstance(): AppServiceConfig {
    if (!AppServiceConfig.instance) {
      AppServiceConfig.instance = new AppServiceConfig();
    }
    return AppServiceConfig.instance;
  }

  /**
   * Initializes the configuration by loading settings
   */
  public async initialize(): Promise<void> {
    this.settings = await appSettingsService.loadSettings();
  }

  /**
   * Gets the current settings (initializes if needed)
   */
  private async getSettings(): Promise<AppSettings> {
    if (!this.settings) {
      await this.initialize();
    }
    return this.settings!;
  }

  // API Configuration
  public async getApiBaseUrl(): Promise<string> {
    const settings = await this.getSettings();
    return settings.Api.BaseUrl;
  }

  public async getApigeeBaseUrl(): Promise<string> {
    const settings = await this.getSettings();
    return settings.Api.ApigeeBaseUrl;
  }

  public async getApiKey(): Promise<string> {
    const settings = await this.getSettings();
    return settings.Api.ApiKey;
  }

  public async getApiTimeout(): Promise<number> {
    const settings = await this.getSettings();
    return settings.Api.TimeoutMs;
  }

  // Authentication Configuration
  public async getAuthConfig(): Promise<AppSettings['Auth']> {
    const settings = await this.getSettings();
    return settings.Auth;
  }

  public async isAuthRequired(): Promise<boolean> {
    const settings = await this.getSettings();
    return settings.Auth.RequireAuthentication;
  }

  public async shouldSkipAuthChecks(): Promise<boolean> {
    const settings = await this.getSettings();
    return settings.Auth.SkipAuthChecks;
  }

  // Service Configuration
  public async shouldUseMockServices(): Promise<boolean> {
    const settings = await this.getSettings();
    return settings.Services.UseMockServices;
  }

  public async getMockSettings(): Promise<AppSettings['Services']['MockSettings']> {
    const settings = await this.getSettings();
    return settings.Services.MockSettings;
  }

  public async getRealTimeSettings(): Promise<AppSettings['Services']['RealTimeSettings']> {
    const settings = await this.getSettings();
    return settings.Services.RealTimeSettings;
  }

  // App Configuration
  public async getAppConfig(): Promise<AppSettings['App']> {
    const settings = await this.getSettings();
    return settings.App;
  }

  public async getEnvironment(): Promise<string> {
    const settings = await this.getSettings();
    return settings.App.Environment;
  }

  // Feature Flags
  public async getFeatures(): Promise<AppSettings['Features']> {
    const settings = await this.getSettings();
    return settings.Features;
  }

  public async isFeatureEnabled(feature: keyof AppSettings['Features']): Promise<boolean> {
    const settings = await this.getSettings();
    return settings.Features[feature];
  }

  /**
   * Legacy compatibility method - returns the same structure as the old ServiceConfig
   * @deprecated Use the specific getter methods instead
   */
  public async getLegacyConfig(): Promise<{
    USE_MOCK_SERVICES: boolean;
    AUTHENTICATION: {
      REQUIRE_AUTHENTICATION: boolean;
      SKIP_AUTH_CHECKS: boolean;
    };
    MOCK_SETTINGS: {
      SIMULATE_NETWORK_DELAY: boolean;
      MIN_DELAY: number;
      MAX_DELAY: number;
    };
    REAL_TIME_SETTINGS: {
      DEFAULT_REFRESH_INTERVAL: number;
    };
  }> {
    const settings = await this.getSettings();
    return {
      USE_MOCK_SERVICES: settings.Services.UseMockServices,
      AUTHENTICATION: {
        REQUIRE_AUTHENTICATION: settings.Auth.RequireAuthentication,
        SKIP_AUTH_CHECKS: settings.Auth.SkipAuthChecks,
      },
      MOCK_SETTINGS: {
        SIMULATE_NETWORK_DELAY: settings.Services.MockSettings.SimulateNetworkDelay,
        MIN_DELAY: settings.Services.MockSettings.MinDelayMs,
        MAX_DELAY: settings.Services.MockSettings.MaxDelayMs,
      },
      REAL_TIME_SETTINGS: {
        DEFAULT_REFRESH_INTERVAL: settings.Services.RealTimeSettings.DefaultRefreshIntervalMs,
      }
    };
  }
}

// Export singleton instance
export const appServiceConfig = AppServiceConfig.getInstance();